import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  Input,
  Output,
} from '@angular/core';
import { PdfPrintService } from './pdfPrintService';

@Component({
  selector: 'ad-pdf-viewer',
  templateUrl: './ad-pdf-viewer.component.html',
  styleUrls: ['./ad-pdf-viewer.component.scss'],
})
export class AdPdfViewerComponent implements OnInit, OnDestroy {
  loading = true;
  @ViewChild('container', { static: true })
  container: ElementRef<HTMLDivElement>;
  defaultScaleDelta = 1.1;
  defaultScaleValue = 'auto';
  get disableZoomIn() {
    return (
      this.pdfViewer === undefined ||
      !this.pdfViewer.pageViewsReady ||
      Number(this.pdfViewer.currentScaleValue) === this.maxScale
    );
  }
  get disableZoomOut() {
    return (
      this.pdfViewer === undefined ||
      !this.pdfViewer.pageViewsReady ||
      Number(this.pdfViewer.currentScaleValue) === this.minScale
    );
  }
  get disablePrint() {
    return (
      this.pdfViewer === undefined ||
      !this.pdfViewer.pageViewsReady ||
      (this.printService !== undefined && this.printService.running)
    );
  }
  link: HTMLLinkElement;
  maxScale = 1.5;
  minScale = 0.7;
  pdf: any;
  pdfjs = {
    core: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.min.js',
    viewer: {
      css: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/web/pdf_viewer.css',
      js: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/web/pdf_viewer.min.js',
    },
    worker:
      'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.worker.min.js',
  };
  pdfViewer: any;
  printContainer: HTMLDivElement;
  printService: PdfPrintService;
  printing = false;
  progress = 0;
  @ViewChild('viewer', { static: true })
  viewer: ElementRef<HTMLDivElement>;

  _fileSrc: string = '';
  @Input() set fileSrc(val) {
    console.log('fileSrc', val);
    this._fileSrc = val;
    if (val) {
      this.loadPdf();
    }
  }

  get fileSrc() {
    return this._fileSrc;
  }

  constructor(private _renderer: Renderer2) {}

  ngOnDestroy(): void {
    this.removeStyle();
    this.removePrinterContainer();
  }

  ngOnInit(): void {
    this.createPrinterContainer();
    this.loadStyle();
    if ((window as any).pdfjsLib === undefined) {
      this.loadJavascript();
    } else {
      this.loadViewer();
    }
  }

  loadJavascript(): void {
    const pdf = this._renderer.createElement('script');
    pdf.src = this.pdfjs.core;
    pdf.setAttribute('async', '');
    pdf.setAttribute('defer', '');
    pdf.onload = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._renderer.removeChild(document.body, pdf);
      (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc =
        this.pdfjs.worker;
      const pdf_viewer = this._renderer.createElement('script');
      pdf_viewer.src = this.pdfjs.viewer.js;
      pdf_viewer.setAttribute('async', '');
      pdf_viewer.setAttribute('defer', '');
      pdf_viewer.onload = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._renderer.removeChild(document.body, pdf_viewer);
        this.loadViewer();
        this.loadPdf();
      };
      this._renderer.appendChild(document.body, pdf_viewer);
    };
    this._renderer.appendChild(document.body, pdf);
  }

  loadPdf(): void {
    if (!this.fileSrc || (window as any).pdfjsLib === undefined) {
      return;
    }
    this.loading = true;
    const loadingTask = (window as any).pdfjsLib.getDocument(this.fileSrc);
    loadingTask.promise.then((pdf) => {
      this.pdf = pdf;
      this.pdfViewer.setDocument(pdf);
      this.loading = false;
      const firstPagePromise = this.pdfViewer.firstPagePromise;
      firstPagePromise.then((pdfPage) => {
        this.pdfViewer.currentScaleValue = this.defaultScaleValue;
        this.pdfViewer.scrollMode = 0;
        this.pdfViewer.SpreadMode = 0;
        this.container.nativeElement.scrollTop = 0;
      });
    });
  }

  loadStyle(): void {
    this.link = this._renderer.createElement('link');
    this.link.setAttribute('rel', 'stylesheet');
    this.link.setAttribute('async', '');
    this.link.setAttribute('defer', '');
    this.link.href = this.pdfjs.viewer.css;
    this._renderer.appendChild(document.body, this.link);
  }

  loadViewer(): void {
    let eventBus = new (window as any).pdfjsViewer.EventBus();
    const pdfViewer = new (window as any).pdfjsViewer.PDFViewer({
      container: this.container.nativeElement,
      enablePrintAutoRotate: false,
      enableWebGL: false,
      maxCanvasPixels: 16777216,
      renderer: 'canvas',
      renderInteractiveForms: false,
      textLayerMode: 0,
      useOnlyCssZoom: false,
      eventBus: eventBus,
      viewer: this.viewer.nativeElement,
    });
    this.pdfViewer = pdfViewer;
    this.pdfViewer.focus();
  }

  createPrinterContainer(): void {
    this.printContainer = this._renderer.createElement('div');
    this.printContainer.setAttribute('class', 'printContainer');
    this._renderer.appendChild(document.body, this.printContainer);
  }

  public print(e: Event): void {
    this.printing = true;
    if (this.printService === undefined) {
      const pagesOverview = this.pdfViewer.getPagesOverview();
      this.printService = new PdfPrintService(
        this.pdf,
        pagesOverview,
        this.printContainer
      );
      this.printService.onProgress.subscribe((data) => {
        this.progress = Math.round((100 * data.index) / data.total);
      });
    }
    if (this.printService.running) {
      return;
    }
    this.progress = 0;
    this.printService.print();
  }

  removePrinterContainer(): void {
    if (this.printContainer) {
      this._renderer.removeChild(document.body, this.printContainer);
    }
  }

  removeStyle(): void {
    if (this.link) {
      this._renderer.removeChild(document.body, this.link);
    }
  }

  public zoomIn(e: Event): void {
    if (this.pdfViewer.isInPresentationMode) {
      return;
    }
    let newScale = this.pdfViewer.currentScale;
    newScale = (newScale * this.defaultScaleDelta).toFixed(2);
    newScale = Math.ceil(newScale * 10) / 10;
    newScale = Math.min(this.maxScale, newScale);
    this.pdfViewer.currentScaleValue = newScale;
  }

  public zoomOut(e: Event): void {
    if (this.pdfViewer.isInPresentationMode) {
      return;
    }
    let newScale = this.pdfViewer.currentScale;
    newScale = (newScale / this.defaultScaleDelta).toFixed(2);
    newScale = Math.floor(newScale * 10) / 10;
    newScale = Math.max(this.minScale, newScale);
    this.pdfViewer.currentScaleValue = newScale;
  }
}
